import { useEffect, useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { FilterGroup, FilterOption } from '@/components/Filter/types';
import useRelatedSkills from '@/modules/corporate/modules/feedback/hooks/useRelatedSkills';
import { PeerFeedbackMindsetRatingInputType } from '@/types/schema';

import { getSkillFilterOption, SkillMenuItem } from '../components/RelatedSkillsOrMindsets/utils';

const useCreateFeedbackRelatedMindsets = ({
  userId,
  skillRatings,
  onChange,
}: {
  userId?: string;
  skillRatings: PeerFeedbackMindsetRatingInputType[];
  onChange: (value: PeerFeedbackMindsetRatingInputType[]) => void;
}) => {
  const [selectedSkillsState, setSelectedSkills] = useState<SkillMenuItem[]>();
  const [open, { toggle }] = useToggle(false);
  const selectedSkillIds = skillRatings.map((selectedSkill) => selectedSkill.mindsetId);
  const { skills, fetching, selectedSkills, searchText, setSearchText } = useRelatedSkills({
    skillIds: selectedSkillIds,
    userId: userId,
    isMindset: true,
    pause: !!selectedSkillsState,
  });

  useEffect(() => {
    if (selectedSkillIds.length && !selectedSkillsState && !fetching) {
      const selectedSkillsMap = selectedSkills?.map((skill) => getSkillFilterOption(skill));
      setSelectedSkills(selectedSkillsMap);
    }
  }, [fetching, selectedSkillIds.length, selectedSkills, selectedSkillsState]);

  const skillRatingMap = useMemo(() => {
    const newSkillRatingMap = {} as Record<string, number>;
    skillRatings.forEach((selectedValue) => {
      if (selectedValue.rating) {
        newSkillRatingMap[selectedValue.mindsetId] = selectedValue.rating;
      }
    });
    return newSkillRatingMap;
  }, [skillRatings]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    return skills?.length && userId
      ? [
          {
            id: 'mindsets',
            options: skills.map((skill) => getSkillFilterOption(skill)),
          },
        ]
      : [];
  }, [skills, userId]);

  const addSkill = useEvent((filterOptions: FilterOption[]) => {
    setSelectedSkills(filterOptions as SkillMenuItem[]);
    const keyBySkillRatings = keyBy(skillRatings, 'mindsetId');
    const newSkillRatings = (filterOptions as SkillMenuItem[]).map((skillOption) => ({
      mindsetId: skillOption.id,
      rating: keyBySkillRatings[skillOption.id]?.rating,
    }));
    onChange(newSkillRatings);
  });

  const removeSkill = useEvent((id: string) => {
    const updatedSkillRatings = skillRatings.filter(
      (skillRating: PeerFeedbackMindsetRatingInputType) => skillRating.mindsetId !== id,
    );
    const updatedSelectedSkillsState = selectedSkillsState?.filter(
      (skillMenuItem: SkillMenuItem) => skillMenuItem.id !== id,
    );
    setSelectedSkills(updatedSelectedSkillsState);
    onChange(updatedSkillRatings);
  });

  const onRatingChange = useEvent((skilId: string, skillRating: number) => {
    const newSkillRating = [...skillRatings];
    const index = newSkillRating.findIndex((skillRating) => skillRating.mindsetId === skilId);
    if (index !== -1) {
      newSkillRating[index].rating = skillRating;
    }
    onChange(newSkillRating);
  });

  return {
    addSkill,
    removeSkill,
    filterGroups,
    selectedSkillsState,
    fetching,
    skillRatingMap,
    onRatingChange,
    searchText,
    setSearchText,
    toggle,
    open,
  };
};

export default useCreateFeedbackRelatedMindsets;
